import { useEffect, useState } from "react";
import { streamChat } from "@/lib/ai";
import api from "@/lib/api";
import { toast } from "sonner";
import { devWarn, devError } from "@/lib/log";

export const msgId = () => Math.random().toString(36).slice(2);
const MAX_FILE_BYTES = 5 * 1024 * 1024;

// Owns chat messages, attachments, and the send/clear flows for the AI assistant.
export function useChatSession(sessionId, open) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (open && sessionId) {
      api
        .get(`/ai/chat/${sessionId}`)
        .then(({ data }) => setMessages(data.map((m) => ({ id: msgId(), role: m.role, content: m.content }))))
        .catch((err) => devWarn("Could not load chat history", err));
    }
  }, [open, sessionId]);

  const addFiles = (files) => {
    Array.from(files || []).forEach((file) => {
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name} is over 5 MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const data = String(reader.result).split(",")[1] || "";
        setAttachments((prev) => [
          ...prev,
          { id: msgId(), name: file.name, mime: file.type || "application/octet-stream", data },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  const appendDelta = (delta) =>
    setMessages((m) => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { ...last, content: last.content + delta };
      return copy;
    });

  const send = async (text) => {
    let msg = (text ?? input).trim();
    if (!msg && attachments.length) msg = "Please review the attached file(s).";
    if (!msg || busy) return;
    const atts = attachments.map(({ name, mime, data }) => ({ name, mime, data }));
    const display = msg + (atts.length ? `\n[Attached: ${atts.map((a) => a.name).join(", ")}]` : "");
    setInput("");
    setAttachments([]);
    setBusy(true);
    setMessages((m) => [
      ...m,
      { id: msgId(), role: "user", content: display },
      { id: msgId(), role: "assistant", content: "" },
    ]);
    try {
      await streamChat(sessionId, msg, appendDelta, atts);
    } catch (err) {
      devError("AI chat failed", err);
      toast.error("AI assistant is unavailable right now");
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (!last.content) copy[copy.length - 1] = { ...last, content: "Sorry, I couldn't respond." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    try {
      await api.delete(`/ai/chat/${sessionId}`);
      setMessages([]);
    } catch (err) {
      devError("Clear chat failed", err);
      toast.error("Could not clear the conversation");
    }
  };

  return { messages, input, setInput, busy, attachments, addFiles, removeAttachment, send, clear };
}
